import React, { useEffect, useRef } from 'react';
import { ElementUpdateTypes, ElementUpdatedEventData, GroupElement, VisualElement } from '@bynder-studio/render-core';
import { Form } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import editorAutocorrect from 'packages/pages/editor/EditorAutocorrectRules';
import useForceUpdate from '~/hooks/useForceUpdate';
import { useEffectivelyLocked } from 'packages/hooks/useEffectivelyLocked';
import InputLinked from '../../../FormComponents/InputLinked';

const Height = ({ creativeModel, creativeType, selectedElement }) => {
    const forceUpdate = useForceUpdate();
    const { translate } = useTranslate();
    const locked = useEffectivelyLocked();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const {
        id,
        dimension: { height },
    } = selectedElement;

    const value = useRef(height);

    const setValue = (val) => {
        value.current = val;

        if (inputRef.current) {
            inputRef.current.value = val;
        }
    };

    const isGroup = selectedElement instanceof GroupElement;

    const applyChanges = (val: number) => {
        const isUniScale = (isGroup || selectedElement.lockUniScaling) && selectedElement.dimension.getHeight();
        const correctValue = editorAutocorrect(
            'element_height',
            val,
            creativeType,
            selectedElement.dimension,
            isUniScale,
        );
        setValue(correctValue);
        const param = {
            dimension: {
                ...selectedElement.dimension,
                height: correctValue,
            },
        };

        if (isUniScale) {
            const k = Number(correctValue) / selectedElement.dimension.getHeight();
            param.dimension.width = Math.ceil(selectedElement.dimension.getWidth() * k);
        }

        if (Math.trunc(selectedElement.dimension.height) !== param.dimension.height) {
            if (isGroup) {
                const scale = Number(correctValue) / selectedElement.dimension.getHeight();

                // update all child elements of a group
                selectedElement.children.forEach((groupElement) => {
                    const groupElParam = {
                        dimension: {
                            width: Math.ceil(groupElement.dimension.getWidth() * scale),
                            height: Math.ceil(groupElement.dimension.getHeight() * scale),
                        },
                        position: {
                            x: groupElement.position.x * scale,
                            y: groupElement.position.y * scale,
                        },
                    };

                    creativeModel.updateElement(groupElement.id, groupElParam);
                });

                creativeModel.updateElement(id, param);
            } else {
                creativeModel.updateElement(id, param);
            }
        }
    };

    const onBlur = () => {
        applyChanges(value.current);
    };

    const onChange = (newValue) => {
        setValue(newValue);
        forceUpdate();
    };

    const onKeyDown = (event) => {
        switch (event.key) {
            case 'Enter':
                onBlur();
                break;
            case 'ArrowUp':
            case 'ArrowDown': {
                event.preventDefault();
                const numberToAdd = event.key === 'ArrowDown' ? -1 : 1;
                const factor = event.shiftKey ? 10 : 1;
                applyChanges(Number(value.current) + numberToAdd * factor);
                break;
            }
        }
    };

    // because value can be changed on the canvas
    useEffect(() => {
        const correctValue = editorAutocorrect('element_height', height, creativeType, selectedElement.dimension);
        setValue(correctValue);
    }, [id, height, creativeType]);

    useEffect(() => {
        const listener = (data: ElementUpdatedEventData<VisualElement>) => {
            if (data.element.id === id && data.updateTypes.has(ElementUpdateTypes.DIMENSION)) {
                setValue(
                    editorAutocorrect(
                        'element_height',
                        data.element.dimension.getHeight(),
                        creativeType,
                        data.element.dimension,
                    ),
                );
            }
        };

        creativeModel.on('elementUpdated', listener);

        return () => creativeModel.off('elementUpdated', listener);
    }, [creativeModel, creativeType, id]);

    return (
        <Form.Group>
            <InputLinked
                inputRef={inputRef}
                name={translate('editor.sidebar.shots.height.label')}
                aria-label={translate('editor.sidebar.shots.height.label')}
                type="number"
                value={Math.ceil(value.current).toString()}
                elementValue={Math.trunc(selectedElement.dimension.height)}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                onChange={onChange}
                isDisabled={locked}
                prefix="H"
                suffix="px"
                data-testid="shots_properties_height"
            />
        </Form.Group>
    );
};

export default Height;
